import {useEffect, useRef, useState} from 'react';

const normalizedCount = (value: any, total: any) => {
  const count = Number(value);
  if (!Number.isFinite(count)) return 0;
  return Math.max(0, Math.min(count, total));
};

export default function useProgressiveCount(
  total: any,
  resetKey = '',
  initialCount = 32,
  step = 32,
  {
    initialVisibleCount = null,
    preserveOnReset = false,
    onCountChange,
    disabled = false,
  }: Record<string, any> = {},
) {
  const [count, setCount] = useState(() => {
    const startingCount =
      initialVisibleCount == null ? initialCount : initialVisibleCount;
    return normalizedCount(startingCount, total);
  });
  const previousTotalRef = useRef(total);
  const countRef = useRef(count);

  useEffect(() => {
    countRef.current = count;
  }, [count]);

  useEffect(() => {
    if (disabled) return;
    onCountChange?.(Math.min(count, total), total);
  }, [count, disabled, onCountChange, total]);

  useEffect(() => {
    if (disabled) {
      setCount(total);
      previousTotalRef.current = total;
      return undefined;
    }

    const previousTotal = previousTotalRef.current;
    previousTotalRef.current = total;

    if (total <= initialCount) {
      setCount(total);
      return undefined;
    }

    const preservedCount =
      preserveOnReset && previousTotal > 0
        ? countRef.current >= previousTotal
          ? total
          : Math.max(countRef.current, initialCount)
        : initialCount;
    const startingCount = normalizedCount(preservedCount, total);

    setCount(startingCount);
    if (startingCount >= total) return undefined;

    let raf = 0;
    const grow = () => {
      setCount(current => {
        const next = Math.min(current + step, total);
        if (next < total) raf = requestAnimationFrame(grow);
        return next;
      });
    };

    raf = requestAnimationFrame(grow);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [disabled, initialCount, preserveOnReset, resetKey, step, total]);

  return disabled ? total : Math.min(count, total);
}
